import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageLayout } from './layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { ArrowLeft, FileText, Calendar, Users, Package, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react'
import {
  fetchUserImportSessions,
  fetchStagingSalesRecords,
  fetchImportSession
} from '../supabase-client'

export default function ImportHistory({ user, onLogout }) {
  const navigate = useNavigate()
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedSession, setSelectedSession] = useState(null)
  const [sessionDetails, setSessionDetails] = useState(null)
  const [stagingRecords, setStagingRecords] = useState([])
  const [showSessionModal, setShowSessionModal] = useState(false)

  // Load import sessions on component mount
  useEffect(() => {
    const loadSessions = async () => {
      if (!user?.id) return

      try {
        setLoading(true)
        const sessionData = await fetchUserImportSessions(user.id)
        setSessions(sessionData)
      } catch (err) {
        console.error('Failed to load import sessions:', err)
        setError('Failed to load import history')
      } finally {
        setLoading(false)
      }
    }

    loadSessions()
  }, [user])

  // Get status badge variant
  const getStatusBadge = (status) => {
    const variants = {
      completed: { variant: 'default', icon: CheckCircle, text: 'Completed' },
      failed: { variant: 'destructive', icon: XCircle, text: 'Failed' },
      abandoned: { variant: 'secondary', icon: AlertTriangle, text: 'Abandoned' },
      importing: { variant: 'default', icon: Clock, text: 'Importing' },
      ready: { variant: 'default', icon: Clock, text: 'Ready' },
      validating: { variant: 'default', icon: Clock, text: 'Validating' },
      mapping: { variant: 'default', icon: Clock, text: 'Mapping' },
      parsing: { variant: 'default', icon: Clock, text: 'Parsing' },
      uploading: { variant: 'default', icon: Clock, text: 'Uploading' }
    }

    return variants[status] || { variant: 'secondary', icon: Clock, text: status }
  }

  // Handle viewing session details
  const handleViewSession = async (session) => {
    try {
      setSelectedSession(session)

      // Fetch detailed session info and staging records
      const [sessionData, records] = await Promise.all([
        fetchImportSession(session.id),
        fetchStagingSalesRecords(session.id)
      ])

      setSessionDetails(sessionData)
      setStagingRecords(records)
      setShowSessionModal(true)

    } catch (err) {
      console.error('Failed to load session details:', err)
      setError('Failed to load session details')
    }
  }

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString()
  }

  // Calculate session statistics
  const getSessionStats = () => {
    const imported = stagingRecords.filter(r => r.imported).length
    const valid = stagingRecords.filter(r => r.validation_status === 'valid').length
    const errors = stagingRecords.filter(r => r.validation_status === 'invalid').length

    return { imported, valid, errors, total: stagingRecords.length }
  }

  if (loading) {
    return (
      <PageLayout
        title="Import History"
        user={user}
        onLogout={onLogout}
      >
        <div className="h-full overflow-y-auto">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <Clock className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-500" />
            <p className="text-gray-600">Loading import history...</p>
          </div>
        </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Import History"
      user={user}
      onLogout={onLogout}
    >
      <div className="h-full overflow-y-auto">
        {/* Page Header */}
        <div className="mb-4">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex justify-between items-center">
            <div>
              <h1 className="page-title">Import History</h1>
              <p className="page-subtitle">View and manage your import sessions</p>
            </div>
            <Button onClick={() => navigate('/import-wizard-v2')}>
              <FileText className="w-4 h-4 mr-2" />
              New Import
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Sessions List */}
        <div className="space-y-4">
          {sessions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-medium mb-2">No Import History</h3>
                <p className="text-gray-600 mb-4">You haven't imported any files yet.</p>
                <Button onClick={() => navigate('/import-wizard-v2')}>
                  Start Your First Import
                </Button>
              </CardContent>
            </Card>
          ) : (
            sessions.map((session) => {
              const statusInfo = getStatusBadge(session.status)
              const StatusIcon = statusInfo.icon

              return (
                <Card key={session.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <FileText className="w-5 h-5 text-gray-600" />
                          <h3 className="font-medium text-lg">{session.file_name}</h3>
                          <Badge variant={statusInfo.variant}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusInfo.text}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {formatDate(session.created_at)}
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            {session.total_rows} rows
                          </div>
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4" />
                            {session.processed_rows || 0} processed
                          </div>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            {session.entity_type}
                          </div>
                        </div>

                        {session.error_message && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-700">{session.error_message}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewSession(session)}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })
          )}
        </div>
      </div>

      {/* Session Details Modal */}
      <Dialog open={showSessionModal} onOpenChange={setShowSessionModal}>
        <DialogContent className="odin-modal-large max-w-4xl">
          <DialogHeader>
            <DialogTitle>Import Session Details</DialogTitle>
            <DialogDescription>
              {selectedSession?.file_name} • {formatDate(selectedSession?.created_at)}
            </DialogDescription>
          </DialogHeader>

          {sessionDetails && (
            <div className="space-y-6 py-6">
              {/* Session Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">
                    {sessionDetails.total_rows}
                  </div>
                  <div className="text-sm text-gray-600">Total Rows</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-700">
                    {getSessionStats().imported}
                  </div>
                  <div className="text-sm text-gray-600">Imported</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-700">
                    {getSessionStats().valid}
                  </div>
                  <div className="text-sm text-gray-600">Valid</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-700">
                    {getSessionStats().errors}
                  </div>
                  <div className="text-sm text-gray-600">Errors</div>
                </div>
              </div>

              {/* Staging Records */}
              {stagingRecords.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium mb-4">Import Records</h3>
                  <div className="max-h-96 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b sticky top-0">
                        <tr>
                          <th className="text-left p-3">Row</th>
                          <th className="text-left p-3">Status</th>
                          <th className="text-left p-3">Customer</th>
                          <th className="text-left p-3">Product</th>
                          <th className="text-left p-3">Quantity</th>
                          <th className="text-left p-3">Imported</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stagingRecords.map((record) => (
                          <tr key={record.id} className="border-b hover:bg-gray-50">
                            <td className="p-3">{record.row_number}</td>
                            <td className="p-3">
                              <Badge variant={record.validation_status === 'valid' ? 'default' : 'destructive'}>
                                {record.validation_status}
                              </Badge>
                            </td>
                            <td className="p-3">{record.mapped_data?.customer_id || 'N/A'}</td>
                            <td className="p-3">{record.mapped_data?.product_id || 'N/A'}</td>
                            <td className="p-3">{record.mapped_data?.quantity || 'N/A'}</td>
                            <td className="p-3">
                              {record.imported ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <XCircle className="w-4 h-4 text-gray-400" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Session Metadata */}
              <div>
                <h3 className="text-lg font-medium mb-4">Session Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Session ID:</span> {sessionDetails.id}
                  </div>
                  <div>
                    <span className="font-medium">Entity Type:</span> {sessionDetails.entity_type}
                  </div>
                  <div>
                    <span className="font-medium">File Size:</span> {(sessionDetails.file_size / 1024).toFixed(1)} KB
                  </div>
                  <div>
                    <span className="font-medium">Created:</span> {formatDate(sessionDetails.created_at)}
                  </div>
                  {sessionDetails.completed_at && (
                    <div>
                      <span className="font-medium">Completed:</span> {formatDate(sessionDetails.completed_at)}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Status:</span> {sessionDetails.status}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  )
}